import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {Picture} from "../../components/Picture";
import {getIntlLocale} from "../../lib/getIntlLocale";
import {i18nFormat} from "../../lib/i18nFormat";
import {urlToPage} from "@shared/lib/urlToPage";
import {subscriptionTypes} from "../../enums/subscriptionType";

const GOOGLE_PLAY_STORE_URL = "https://play.google.com/store/account/subscriptions";
const APPLE_APP_STORE_URL = "https://apps.apple.com/account/subscriptions";


export function Subscription(props) {
  const intlLocale = getIntlLocale();
  const backupsImage = "/img/pages/account/subscriptions/homey-pro-backups.png";
  const developerImage = "/img/pages/account/subscriptions/homey-verified-developer.png";
  const premiumImage = "/img/pages/account/subscriptions/premium.png";
  const selfHostedServerImage = "/img/pages/account/subscriptions/self-hosted-server.png";

  // Normalize subscriptionId for self-hosted servers
  const subscriptionId = useMemo(() => {
    if (props.subscriptionId.startsWith(subscriptionTypes.HomeySelfHostedServersMonthly)) {
      return subscriptionTypes.HomeySelfHostedServersMonthly;
    }
    return props.subscriptionId;
  }, [props.subscriptionId]);


  function getTitle() {
    switch (subscriptionId) {
      case subscriptionTypes.HomeyDeveloperAthom:
        return i18nFormat("subscriptions.homey-verified-developer.title");
      case subscriptionTypes.HomeyPlusAthom:
      case subscriptionTypes.HomeyPlusMonthlyApple:
      case subscriptionTypes.HomeyPlusMonthlyGoogle:
        return i18nFormat("subscriptions.homey-pro-backups.title");
      case subscriptionTypes.HomeyPremiumMonthlyApple:
      case subscriptionTypes.HomeyPremiumMonthlyGoogle:
        return i18nFormat("subscriptions.homey-premium.title");
      case subscriptionTypes.HomeySelfHostedServersMonthly:
        return i18nFormat("subscriptions.homey-self-hosted-server.title");
      default:
        return null;
    }
  }

  function getImage() {
    switch (subscriptionId) {
      case subscriptionTypes.HomeyDeveloperAthom:
        return (
          <Picture
            className="account-subscription__image"
            src={developerImage}
            alt={i18nFormat("subscriptions.homey-verified-developer.image-alt")}
          />
        );
      case subscriptionTypes.HomeyPlusAthom:
      case subscriptionTypes.HomeyPlusMonthlyApple:
      case subscriptionTypes.HomeyPlusMonthlyGoogle:
        return (
          <Picture
            className="account-subscription__image"
            src={backupsImage}
            alt={i18nFormat("subscriptions.homey-pro-backups.image-alt")}
          />
        );
      case subscriptionTypes.HomeyPremiumMonthlyApple:
      case subscriptionTypes.HomeyPremiumMonthlyGoogle:
        return (
          <Picture
            className="account-subscription__image"
            src={premiumImage}
            alt={i18nFormat("subscriptions.homey-premium.image-alt")}
          />
        );
      case subscriptionTypes.HomeySelfHostedServersMonthly:
        return (
          <Picture
            className="account-subscription__image"
            src={selfHostedServerImage}
            alt={i18nFormat("subscriptions.homey-self-hosted-server.image-alt")}
          />
        );
      default:
        return null;
    }
  }

  function getStatus() {
    const dateNow = new Date();
    const expirationDate = new Date(props.subscription.expiresAt);
    const status = dateNow > expirationDate ? "inactive" : "active";
    return (
      <span className={`account-subscription-status account-subscription-status--${status}`}>
        {i18nFormat(`subscriptions.status.${status}`)}
      </span>
    );
  }

  function formatDate(date) {
    return date.toLocaleDateString(intlLocale, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }

  function getExpirationDate() {
    const dateNow = new Date();
    const expirationDate = new Date(props.subscription.expiresAt);

    if (dateNow >= expirationDate && !props.subscription.autoRenew) {
      return (
        <span className="account-subscription-expiration-date">
          {i18nFormat("subscriptions.status.expired-since", { date: formatDate(expirationDate) })}
        </span>
      )
    }
    if (dateNow < expirationDate && !props.subscription.autoRenew) {
      return (
        <span className="account-subscription-expiration-date">
          {i18nFormat("subscriptions.status.expires-on", { date: formatDate(expirationDate) })}
        </span>
      )
    }
    if (dateNow < expirationDate && props.subscription.autoRenew) {
      return (
        <span className="account-subscription-expiration-date">
          {i18nFormat("subscriptions.status.renews-on", { date: formatDate(expirationDate) })}
        </span>
      )
    }
    return null;
  }

  function getNote() {
    switch (subscriptionId) {
      case subscriptionTypes.HomeyDeveloperAthom:
        return (
          <span className="account-subscription__note">
          {i18nFormat("subscriptions.homey-verified-developer.verified-since", { date: formatDate(new Date(props.subscription.startedAt)) })}
          </span>
        );
      case subscriptionTypes.HomeyPlusMonthlyApple:
      case subscriptionTypes.HomeyPremiumMonthlyApple:
        return (
          <span className="account-subscription__note">
            {i18nFormat("subscriptions.manage-app-store", {
              link: (
                <a href={APPLE_APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                   className="account-subscription__note-link">
                  <span>{i18nFormat("subscriptions.link-to-app-store")}</span>
                  <span className="glyph-core-square-arrow-up-right"/>
                </a>
              )
            })}
          </span>
        );
      case subscriptionTypes.HomeyPlusMonthlyGoogle:
      case subscriptionTypes.HomeyPremiumMonthlyGoogle:
        return (
          <span className="account-subscription__note">
            {i18nFormat("subscriptions.manage-play-store", {
              link: (
                <a href={GOOGLE_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                   className="account-subscription__note-link">
                  <span>{i18nFormat("subscriptions.link-to-play-store")}</span>
                  <span className="glyph-core-square-arrow-up-right"/>
                </a>
              )
            })}
          </span>
        );
      case subscriptionTypes.HomeySelfHostedServersMonthly:
        return (
          <span className="account-subscription__note">
            {i18nFormat("subscriptions.homey-self-hosted-server.manage")}
          </span>
        );
      default:
        break;
    }
  }

  function getActions() {
    switch (subscriptionId) {
      case subscriptionTypes.HomeyPlusAthom:
        return (
          <a href={urlToPage("homey-pro-backups")} className="account-subscription-action-button">
            <span>{i18nFormat("subscriptions.link-to-store")}</span>
            <span className="glyph-core-square-arrow-up-right"/>
          </a>
        );
      default:
        return null;
    }
  }

  return (
    <div className="account-subscription">
      <div className="account-subscription__component">
        {getImage()}
        <div className="account-subscription__content">
          <div className="account-subscription__info">
            <h3 className="account-subscription__name">{getTitle()}</h3>
            <div className="account-subscription__details">
              {getStatus()}
              {subscriptionId !== subscriptionTypes.HomeyDeveloperAthom && getExpirationDate()}
            </div>
          </div>
          <div className="account-subscription__footer">
            {getNote()}
            {getActions()}
          </div>
        </div>
      </div>
    </div>
  );
}

Subscription.propTypes = {
  subscriptionId: PropTypes.string.isRequired,
  subscription: PropTypes.shape({
    expiresAt: PropTypes.string.isRequired,
    startedAt: PropTypes.string.isRequired,
    autoRenew: PropTypes.bool.isRequired,
  }).isRequired,
}